import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
});

export const PLACEHOLDER_IMAGE = "https://i.ibb.co/jZGBt9RR/placeholder-image.png";

// Auth API
export const authApi = {
    discordLogin: () => api.get('/auth/discord/login'),
    discordCallback: (code) => api.get(`/auth/discord/callback?code=${code}`),
    getSession: (sessionId) => api.get(`/auth/session/${sessionId}`),
    logout: (sessionId) => api.post(`/auth/logout/${sessionId}`),
    getConfig: () => api.get('/auth/config'),
};

// Whitelist API
export const whitelistApi = {
    getAll: () => api.get('/whitelist'),
    add: (discord_id, note) => api.post('/whitelist', { discord_id, note }),
    remove: (discord_id) => api.delete(`/whitelist/${discord_id}`),
    check: (discord_id) => api.get(`/whitelist/check/${discord_id}`),
};

// Islands API (legacy - kept for compatibility)
export const islandsApi = {
    getAll: () => api.get('/islands'),
    getById: (id) => api.get(`/islands/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/islands?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/islands/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/islands/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/islands/reorder/batch', entries),
};

// Gacha Islands API (separate from Upgrade Islands)
export const gachaIslandsApi = {
    getAll: () => api.get('/gacha-islands'),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/gacha-islands?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/gacha-islands/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/gacha-islands/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/gacha-islands/reorder/batch', entries),
};

// Upgrade Islands API (separate from Gacha Islands)
export const upgradeIslandsApi = {
    getAll: () => api.get('/upgrade-islands'),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/upgrade-islands?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/upgrade-islands/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/upgrade-islands/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/upgrade-islands/reorder/batch', entries),
};

// Gachas API (Island-based)
export const gachasApi = {
    getAll: (islandId) => api.get('/gachas', { params: { island_id: islandId } }),
    getById: (id) => api.get(`/gachas/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/gachas?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/gachas/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/gachas/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/gachas/reorder/batch', entries),
};

// Upgrades API (Island-based)
export const upgradesApi = {
    getAll: (islandId) => api.get('/upgrades', { params: { island_id: islandId } }),
    getById: (id) => api.get(`/upgrades/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/upgrades?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/upgrades/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/upgrades/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/upgrades/reorder/batch', entries),
};

// Achievements API
export const achievementsApi = {
    getAll: () => api.get('/achievements'),
    getById: (id) => api.get(`/achievements/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/achievements?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/achievements/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/achievements/${id}?${params.toString()}`);
    },
};

// Wiki Entries API (Relics, Tutorials, Accessories)
export const entriesApi = {
    getAll: (category) => api.get('/entries', { params: { category } }),
    getById: (id) => api.get(`/entries/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/entries?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/entries/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/entries/${id}?${params.toString()}`);
    },
};

// Rank Table API
export const rankTableApi = {
    getAll: () => api.get('/rank-table'),
    create: (data) => api.post('/rank-table', data),
    update: (id, data) => api.put(`/rank-table/${id}`, data),
    delete: (id) => api.delete(`/rank-table/${id}`),
    reorder: (entries) => api.put('/rank-table/reorder/batch', entries),
};

// User Progress API
export const userProgressApi = {
    getAll: (userId, category) => api.get(`/user-progress/${userId}`, { params: { category } }),
    getItem: (userId, itemId) => api.get(`/user-progress/${userId}/${itemId}`),
    updateItem: (userId, itemId, category, data) => api.put(`/user-progress/${userId}/${itemId}?category=${category}`, data),
};

// Reports API
export const reportsApi = {
    getAll: (status) => api.get('/reports', { params: { status } }),
    create: (data) => api.post('/reports', data),
    updateStatus: (id, status) => api.put(`/reports/${id}/status?status=${status}`),
    delete: (id) => api.delete(`/reports/${id}`),
};

// Patch Notes API
export const patchNotesApi = {
    getAll: () => api.get('/patch-notes'),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/patch-notes?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/patch-notes/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/patch-notes/${id}?${params.toString()}`);
    },
};

// Team API
export const teamApi = {
    getAll: () => api.get('/team'),
    create: (data, userId, username) => api.post(`/team?user_id=${userId}&username=${encodeURIComponent(username)}`, data),
    update: (id, data, userId, username) => api.put(`/team/${id}?user_id=${userId}&username=${encodeURIComponent(username)}`, data),
    delete: (id, userId, username) => api.delete(`/team/${id}?user_id=${userId}&username=${encodeURIComponent(username)}`),
};

// Meta Builds API
export const metaBuildsApi = {
    getAll: () => api.get('/meta-builds'),
    getById: (id) => api.get(`/meta-builds/${id}`),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/meta-builds?${params.toString()}`, data);
    },
    update: (id, data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.put(`/meta-builds/${id}?${params.toString()}`, data);
    },
    delete: (id, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.delete(`/meta-builds/${id}?${params.toString()}`);
    },
    reorder: (entries) => api.put('/meta-builds/reorder/batch', entries),
};

// Stats API
export const statsApi = {
    getRelicStats: () => api.get('/stats/relics'),
};

// Rarities API
export const raritiesApi = {
    getAll: () => api.get('/rarities'),
};

// Site Settings API
export const settingsApi = {
    getSiteLock: () => api.get('/settings/site-lock'),
    setSiteLock: (page, locked) => api.put(`/settings/site-lock?page=${page}&locked=${locked}`),
};

// Global Search API with filters
export const searchApi = {
    search: (query, categories, rarity, limit = 50) => api.get('/search', { params: { q: query, categories, rarity, limit } }),
    getSuggestions: (query, limit = 10) => api.get('/search/suggestions', { params: { q: query, limit } }),
};

// Image Upload API
export const uploadApi = {
    uploadImage: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteImage: (filename) => api.delete(`/upload/image/${filename}`),
};

// Favorites API - REMOVED (functionality disabled)

// Templates API
export const templatesApi = {
    getAll: (category) => api.get('/templates', { params: { category } }),
    create: (data, userId, username) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (username) params.append('username', username);
        return api.post(`/templates?${params.toString()}`, data);
    },
    delete: (id) => api.delete(`/templates/${id}`),
};

// Duplicate Check API
export const duplicateCheckApi = {
    check: (name, category, excludeId) => api.get('/check-duplicate', { 
        params: { name, category, exclude_id: excludeId } 
    }),
};

// Activity Logs API
export const activityLogsApi = {
    getAll: (limit = 100, skip = 0, userId, action) => api.get('/activity-logs', { params: { limit, skip, user_id: userId, action } }),
};

// Notifications API
export const notificationsApi = {
    getAll: (userId, limit = 50) => api.get('/notifications', { params: { user_id: userId, limit } }),
    markRead: (notificationId, userId) => api.post(`/notifications/${notificationId}/read?user_id=${userId}`),
    dismiss: (notificationId, userId) => api.post(`/notifications/${notificationId}/dismiss?user_id=${userId}`),
};

// Custom Roles API
export const rolesApi = {
    getAll: () => api.get('/roles'),
    create: (data) => api.post('/roles', data),
    update: (id, data) => api.put(`/roles/${id}`, data),
    delete: (id) => api.delete(`/roles/${id}`),
};

// Backup/Export API
export const backupApi = {
    exportDatabase: () => api.get('/backup/export'),
    exportUserProgress: (userId) => api.get(`/export/user-progress/${userId}`),
    // New backup endpoints
    exportAll: (userId, username) => api.post(`/backup/export?user_id=${userId}&username=${encodeURIComponent(username)}`),
    downloadBackup: (filename) => `${BACKEND_URL}/api/backup/download/${filename}`,
    importAll: (file, userId, username) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/backup/import?user_id=${userId}&username=${encodeURIComponent(username)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getHistory: (params) => api.get('/backup/history', { params }),
    getAutoBackups: () => api.get('/backup/auto-backups'),
    restoreAutoBackup: (filename, userId, username) => 
        api.post(`/backup/restore/${filename}?user_id=${userId}&username=${encodeURIComponent(username)}`),
    createAutoBackup: () => api.post('/backup/create-auto'),
};

// Dashboard API
export const dashboardApi = {
    getProgress: (userId) => api.get(`/dashboard/progress/${userId}`),
};

// Compare API
export const compareApi = {
    compareItems: (itemIds, itemType) => api.post('/compare', { item_ids: itemIds, item_type: itemType }),
};

// Codes API
export const codesApi = {
    getAll: (activeOnly = false) => api.get('/codes', { params: { active_only: activeOnly } }),
    create: (data, userId, username) => api.post('/codes', data, { params: { user_id: userId, username } }),
    update: (id, data, userId, username) => api.put(`/codes/${id}`, data, { params: { user_id: userId, username } }),
    delete: (id, userId, username) => api.delete(`/codes/${id}`, { params: { user_id: userId, username } }),
    reorder: (entries) => api.put('/codes/reorder/batch', entries),
};

export default api;
